import { Injectable } from '@angular/core';
import { Subject, Observable, zip, of } from 'rxjs';
import { withLatestFrom, map, concatMap } from 'rxjs/operators';
import * as jsPDF from 'jspdf';
import 'jspdf-autotable';
import { State, getDashboardCombined, __config_contact } from '../../reducers';
import { CombinedState as DashboardCombinedState } from '../../reducers/dashboard/reducers-dashboard';
import { turnoverOptions, dateTypeOptions } from '../../models/dashboard/option-values';
import {
  formatNumber,
  formatPercentage,
  formatDetailsValue,
  getFilterBySymbol,
  getFilterBySymbolForDetails
} from '../utils/data-helpers';
import { colors } from '../../models/dashboard/chart-options';
import * as chartJs from 'chart.js';
import { DoughnutChartDirective } from '../../../directives/doughnutChart.directive';
import { Store, select } from '@ngrx/store';
import { camelize } from '../utils/data-helpers';
import { AcoInfo } from '../../models/contact';
import { HttpClient } from '@angular/common/http';

const LM = 125; // left margin for content
const TM = 45; // top margin for content
interface Assets {
  fontSourceHanSansCN: string;
  fontMeiryo: string;
  fontRegular: string;
  fontBold: string;
  logoHeader: string;
  logoFooter: string;
}

/**
 * Converts dashboard state into pdf
 */
@Injectable()
export class PdfService {
  private readonly trigger: Subject<null>;
  private readonly result$: Observable<jsPDF>;
  private mainChart: chartJs;
  private doughnutChart: DoughnutChartDirective;
  private pageWidth: number;
  private pageHeight: number;
  private contentWidth: number;
  private lastY = 0;
  private filterBySymbol = '';
  private assets: Assets;
  private readonly assetLinks: Assets = {
    fontSourceHanSansCN: '/assets/fonts/sans-cn/SourceHanSansCN-normal.base64',
    fontMeiryo: '/assets/fonts/meiryo/Meiryo.base64',
    fontRegular: '/assets/fonts/source-sans-pro-full/sourcesanspro-full-regular.base64',
    fontBold: '/assets/fonts/source-sans-pro-full/sourcesanspro-full-bold.base64',
    logoHeader: '/assets/images/pdf/logo-header.base64',
    logoFooter: '/assets/images/pdf/logo-footer.base64'
  };

  private market = '';
  private fontNameBold = '';
  private fontNameRegular = '';

  constructor(private readonly store: Store<State>, private readonly http: HttpClient) {
    this.trigger = new Subject();
    this.result$ = this.trigger.pipe(
      concatMap(this.loadAssets.bind(this)),
      withLatestFrom(
        this.store.pipe(select(getDashboardCombined)),
        this.store.pipe(
          select(__config_contact),
          map(contactConfig => contactConfig.config)
        )
      ),
      map(([_, dashboardState, contactConfig]) => this.render(dashboardState, contactConfig))
    );
  }

  // subscribe on result to get updated file
  getResult(): Observable<jsPDF> {
    return this.result$;
  }

  // call update when you need to generate csv data for current values
  update() {
    this.trigger.next(null);
  }

  getFileName(): string {
    const d = new Date();
    const dParts = [d.getDate(), d.getMonth() + 1, d.getFullYear()];
    return dParts.join('_') + '_AmadeusDirect_dashboard.pdf';
  }

  setMainChartRef(ref: chartJs) {
    this.mainChart = ref;
  }

  setDoughnutChartRef(ref: DoughnutChartDirective) {
    this.doughnutChart = ref;
  }

  private loadAssets(): Observable<null> {
    return this.assets
      ? of(null)
      : zip(
          ...Object.keys(this.assetLinks).map(link => this.http.get(this.assetLinks[link], { responseType: 'text' }))
        ).pipe(
          map(responses => {
            this.assets = {} as Assets;
            Object.keys(this.assetLinks).forEach((v, i) => (this.assets[v] = responses[i]));
            return null;
          })
        );
  }

  private render(dashboardState: DashboardCombinedState, contactConfig: AcoInfo): jsPDF {
    const pdf = new jsPDF({
      orientaion: 'p',
      unit: 'px',
      fromat: 'p4',
      hotfixes: ['px_scaling']
    });
    this.setCustomFonts(pdf);
    this.pageWidth = pdf.internal.pageSize.getWidth();
    this.pageHeight = pdf.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - 2 * LM + 80;
    this.renderHeader(pdf);
    this.renderFilters(pdf, dashboardState);
    this.renderChart(pdf, dashboardState);

    if (dashboardState.report.data.companyReports && dashboardState.report.data.companyReports.length) {
      this.renderDoughnut(pdf, dashboardState);
      this.filterBySymbol = getFilterBySymbol(dashboardState);
      this.renderStatistics(pdf, dashboardState);
      this.filterBySymbol = getFilterBySymbolForDetails(dashboardState);
      this.renderDetails(pdf, dashboardState);
    }
    this.renderFooter(pdf, contactConfig);
    return pdf;
  }

  private setCustomFonts(pdf: jsPDF): void {
    this.market = sessionStorage.getItem('preferredLocale');

    pdf.addFileToVFS('sourcesanspro-full-regular.ttf', this.assets.fontRegular);
    pdf.addFont('sourcesanspro-full-regular.ttf', 'Source-Sans-Pro-Regular', 'normal');
    pdf.addFileToVFS('sourcesanspro-full-bold.ttf', this.assets.fontBold);
    pdf.addFont('sourcesanspro-full-bold.ttf', 'Source-Sans-Pro-Bold', 'normal');
    pdf.addFileToVFS('Meiryo-normal.ttf', this.assets.fontMeiryo);
    pdf.addFont('Meiryo-normal.ttf', 'Meiryo', 'normal');
    pdf.addFileToVFS('SourceHanSansCN-normal.ttf', this.assets.fontSourceHanSansCN);
    pdf.addFont('SourceHanSansCN-normal.ttf', 'SourceHanSansCN', 'normal');
    pdf.setFont('Source-Sans-Pro-Bold');
    pdf.setFont('Source-Sans-Pro-Regular');
    pdf.setFont('Meiryo');
    pdf.setFont('SourceHanSansCN');

    switch (this.market) {
      case 'ja_JP':
        this.fontNameBold = 'Meiryo';
        this.fontNameRegular = 'Meiryo';
        break;

      // Meiryo should be enough, need test on TW market, and after it we can delete the SourceHanSansCN font
      case 'zh_Hant_TW':
        this.fontNameBold = 'Meiryo';
        this.fontNameRegular = 'Meiryo';
        break;

      default:
        this.fontNameBold = 'Source-Sans-Pro-Bold';
        this.fontNameRegular = 'Source-Sans-Pro-Regular';
        break;
    }
  }

  private renderHeader(pdf: jsPDF) {
    pdf.addImage(this.assets.logoHeader, 'PNG', LM, TM + 25, 230, 31);
    pdf.setFillColor(244, 244, 244);
    pdf.rect(LM - 80, TM + 87, this.contentWidth + 80, 50, 'F');
    pdf.setFontSize(18);
    pdf.setTextColor(0, 94, 184);
    const dashboardPersonalPerfomance = $localize`:@@pdf.service.dashboardPersonalPerformance:Performance`;
    pdf.text(dashboardPersonalPerfomance, LM, TM + 120);
    this.lastY = TM + 140;
  }

  private renderFilters(pdf: jsPDF, dashboardState: DashboardCombinedState) {
    const turnoverOption = turnoverOptions.find(({ value }) => value === dashboardState.options.filters.turnover);
    const dateTypeOption = dateTypeOptions.find(({ value }) => value === dashboardState.options.filters.dateType);
    pdf.setTextColor(101, 101, 101);
    pdf.setFontSize(12);
    const ty = this.lastY + 35;
    const lh = 20;
    const dashboardFilteredby = $localize`:@@pdf.service.dashboardFilteredby:Filtered By`;
    const dashboardOptionsStartDate = $localize`:@@pdf.service.dashboardOptionsStartDate:Start date`;
    const dashboardOptionsDate = $localize`:@@pdf.service.dashboardOptionsDate:Date Type`;
    const dashboardOptionsEndDate = $localize`:@@pdf.service.dashboardOptionsEndDate:End Date`;
    [
      [
        dashboardFilteredby + ': ',
        turnoverOption.label,
        dashboardOptionsStartDate + ': ',
        dashboardState.options.filters.dateFrom
          .split('-')
          .reverse()
          .join('/')
      ],
      [
        dashboardOptionsDate + ': ',
        dateTypeOption.label,
        dashboardOptionsEndDate + ': ',
        dashboardState.options.filters.dateTo
          .split('-')
          .reverse()
          .join('/')
      ]
    ].forEach((v, i) => {
      pdf.setFont(this.fontNameRegular);
      pdf.text(v[0], LM, ty + i * lh);
      const tw1 = pdf.getTextWidth(v[0]);
      pdf.setFont(this.fontNameBold);
      pdf.text(v[1], LM + tw1, ty + i * lh);
      const tw4 = pdf.getTextWidth(v[3]);
      pdf.setFont(this.fontNameRegular);
      const tw3 = pdf.getTextWidth(v[2]);
      pdf.text(v[2], LM + this.contentWidth - tw4 - tw3, ty + i * lh);
      pdf.setFont(this.fontNameBold);
      pdf.text(v[3], LM + this.contentWidth - tw4, ty + i * lh);
      pdf.setFont(this.fontNameRegular);
    });
    this.lastY += 75;
  }

  private renderChart(pdf: jsPDF, dashboardState: DashboardCombinedState) {
    if (!this.mainChart) {
      return;
    }
    pdf.addImage(this.getChartImage(640, 320), 'PNG', LM - 20, this.lastY + 20, 640, 320);
    this.lastY += 340;
    if (['CAR', 'HOTEL'].indexOf(dashboardState.options.filters.turnover) > -1) {
      return;
    }
    pdf.setFillColor(244, 244, 244);
    pdf.rect(LM, this.lastY, this.contentWidth, 30, 'F');
    const legendItems = {};
    dashboardState.report.data.bookingReport.bookings.forEach(booking => {
      Object.entries(booking.values).forEach(([key, value]) => {
        if (value) {
          legendItems[key] = true;
        }
      });
    });
    let legendWidth = 20;
    pdf.setFontSize(10);
    Object.entries(colors)
      .filter(([key, _]) => legendItems[key])
      .reverse()
      .forEach(([key, value]) => {
        let label = '';
        switch (camelize(key)) {
          case 'Completed':
            label = $localize`:@@pdf.service.dashboardCompleted:Completed`;
            break;
          case 'Ticketed':
            label = $localize`:@@pdf.service.dashboardTicketed:Ticketed`;
            break;
          case 'Unticketed':
            label = $localize`:@@pdf.service.dashboardUnticketed:Unticketed`;
            break;
          default:
            break;
        }
        legendWidth += pdf.getTextWidth(label);
        pdf.text(label, LM + this.contentWidth - legendWidth, this.lastY + 20);
        pdf.setFillColor(value);
        pdf.rect(LM + this.contentWidth - legendWidth - 25, this.lastY + 8, 15, 15, 'F');
        legendWidth += 45;
      });
    this.lastY += 30;
  }

  private renderDoughnut(pdf: jsPDF, dashboardState: DashboardCombinedState) {
    if (this.doughnutChart) {
      const selectedReport = dashboardState.report.data.companyReports[dashboardState.options.selectedReport];
      const percentage = formatPercentage(selectedReport.percentage) + '%';
      const title = selectedReport.title;
      const textWidth = Math.max(pdf.getTextWidth(percentage), pdf.getTextWidth(title));
      const margin = LM + (this.contentWidth - 80 - textWidth - 128 - 20) / 2;
      pdf.addImage(this.doughnutChart.ctx.canvas.toDataURL(), 'PNG', margin, this.lastY + 50, 128, 128);

      pdf.setFont(this.fontNameBold);

      pdf.setFontSize(24);
      pdf.setTextColor(0, 61, 119);
      pdf.text(percentage, margin + 128 + 20, this.lastY + 100);
      pdf.setFontSize(18);
      pdf.setTextColor(51, 51, 51);
      pdf.text(title, margin + 128 + 20, this.lastY + 140);
      this.lastY += 170;
    }
  }

  private renderStatistics(pdf: jsPDF, dashboardState: DashboardCombinedState) {
    const rows = [];
    dashboardState.report.data.companyReports.forEach((record, i) => {
      rows.push([
        i + 1,
        record.title,
        formatNumber(record.value, this.filterBySymbol) + ' ' + this.filterBySymbol,
        formatPercentage(record.percentage) + '%'
      ]);
    });
    // @ts-ignore
    pdf.autoTable({
      startY: this.lastY + 30,
      margin: { left: LM },
      tableWidth: this.contentWidth,
      columnStyles: {
        0: { cellWidth: 25 },
        2: { halign: 'center' },
        3: { halign: 'right' }
      },
      alternateRowStyles: { fillColor: '#f4f4f4' },
      body: rows,
      styles: { font: this.fontNameRegular }
    });
    // @ts-ignore
    this.lastY = pdf.previousAutoTable.finalY;
  }

  private renderDetails(pdf: jsPDF, dashboardState: DashboardCombinedState) {
    this.lastY += 20;
    if (this.lastY > 820) {
      this.addPage(pdf);
    }
    if (['CAR', 'HOTEL'].indexOf(dashboardState.options.filters.turnover) > -1) {
      return;
    }
    pdf.setTextColor(101, 101, 101);
    pdf.setFontSize(12);
    const dashboardPdfDetailedinfo = $localize`:@@pdf.service.dashboardPdfDetailedinfo:Detailed Info`;
    pdf.text(dashboardPdfDetailedinfo, LM, this.lastY + 40);
    this.lastY += 50;
    dashboardState.report.data.companyReports.forEach((record, i) => {
      const rows = record.details.map(d => {
        const [key, value] = Object.entries(d)[0];
        const dashboard = $localize`:@@pdf.service.dashboard:Dashboard`;
        return ['', dashboard, value && formatDetailsValue({ key, value }, this.filterBySymbol)];
      });
      const lastColId = rows[0].length - 1;

      pdf['autoTable']({
        startY: this.lastY,
        margin: { left: LM },
        tableWidth: this.contentWidth,
        headStyles: { fillColor: '#e7f2fa' },

        columnStyles: {
          0: { cellWidth: 20 },
          [lastColId]: { halign: 'right' }
        },
        alternateRowStyles: { fillColor: false },
        pageBreak: 'auto',
        head: [
          [
            {
              content: i + 1,
              styles: {
                cellWidth: 10,
                textColor: [25, 25, 25]
              }
            },
            {
              content: record.title,
              colSpan: lastColId,
              styles: { textColor: [0, 94, 184] }
            }
          ]
        ],
        body: rows,
        styles: { font: this.fontNameBold }
      });

      this.lastY = pdf['previousAutoTable'].finalY;
    });
  }

  /**
   * Clones chart if we want custom dimensions
   */
  private getChartImage(width?: number, height?: number) {
    if (!width || !height) {
      return this.mainChart.canvas.toDataURL();
    }
    const originalCanvas = this.mainChart.canvas;
    const div = document.createElement('div');
    div.style.height = height + 'px';
    div.style.width = width + 'px';
    document.documentElement.appendChild(div);
    const pdfCanvas = document.createElement('canvas');
    div.appendChild(pdfCanvas);
    this.mainChart.canvas = pdfCanvas;
    this.mainChart.ctx = pdfCanvas.getContext('2d');
    this.mainChart.resize();
    const image = pdfCanvas.toDataURL();
    this.mainChart.canvas = originalCanvas;
    this.mainChart.ctx = originalCanvas.getContext('2d');
    this.mainChart.resize();
    pdfCanvas.remove();
    div.remove();
    return image;
  }

  private renderFooter(pdf: jsPDF, contactConfig: AcoInfo) {
    this.lastY += 30;
    if (this.lastY + 105 > this.pageHeight) {
      this.addPage(pdf);
    }
    pdf.setFillColor(244, 244, 244);
    pdf.rect(LM - 80, this.lastY, this.contentWidth + 80, 105, 'F');
    pdf.addImage(this.assets.logoFooter, 'PNG', LM, this.lastY + 15, 115, 23);
    pdf.setFontSize(10);
    pdf.setTextColor(101, 101, 101);

    pdf.setFont(this.fontNameBold);

    let tw = pdf.getTextWidth(contactConfig.name);
    pdf.text(contactConfig.name, LM + this.contentWidth - tw - 10, this.lastY + 55);

    pdf.setFont(this.fontNameRegular);

    tw = pdf.getTextWidth(contactConfig.address);
    pdf.text(contactConfig.address, LM + this.contentWidth - tw - 10, this.lastY + 72);
    tw = pdf.getTextWidth(contactConfig.phone);
    pdf.text(contactConfig.phone, LM + this.contentWidth - tw - 10, this.lastY + 89);
    this.lastY += 105;
  }

  private addPage(pdf: jsPDF) {
    pdf.addPage();
    this.lastY = TM;
  }
}
